import type { FormEvent } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';
// eslint-disable-next-line import/no-named-as-default
import DOMPurify from 'dompurify';

import { Block, Heading } from '@klp/design';
import type { ITableConsumerState, TableColumnValue } from '@klp/design/Table';
import { Table, TableColumnDef } from '@klp/design/Table';
import { FileGallery, MimeTypes } from '@klp/file';
import type { FileObject } from '@klp/file/classes';
import { Form, FormButton } from '@klp/form';
import { KeyValueItem, KeyValueItems } from '@klp/keyvalueitem';

import { supportFormApiRoutes } from '../api';
import {
    governmentSupportMonths,
    revenueMonths,
} from '../classes/KLPSupportForm';
import type {
    IGovernmentSupportMonth,
    IRevenueMonth,
    KLPSupportForm,
} from '../classes/KLPSupportForm';

interface IProps {
    klpSupportForm?: KLPSupportForm;
    onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

export type ISupportApplicationFormView = IProps;

export const KlpSupportFormView: React.FC<ISupportApplicationFormView> = ({
    klpSupportForm,
    onSubmit,
}) => {
    const { t } = useTranslation();

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (onSubmit) {
            onSubmit(event);
        }
    };

    const renderGovernmentSupportCaseNumber = <DataType, FilterType>(
        _value: TableColumnValue,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<DataType, FilterType>,
        row: IGovernmentSupportMonth,
    ) => (
        <Block klpTypographyVariant="small">
            {
                klpSupportForm?.[
                    `governmentSupportCaseNumber2020${row.monthNumber}`
                ]
            }
        </Block>
    );

    const renderGovernmentSupportAmount = <DataType, FilterType>(
        _value: TableColumnValue,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<DataType, FilterType>,
        row: IGovernmentSupportMonth,
    ) => (
        <Block klpTypographyVariant="small">
            {klpSupportForm?.[`governmentSupportAmount2020${row.monthNumber}`]}
        </Block>
    );

    const renderRevenue =
        (year: number) =>
        <DataType, FilterType>(
            _value: TableColumnValue,
            _column: TableColumnDef<DataType, FilterType>,
            _context: ITableConsumerState<DataType, FilterType>,
            row: IRevenueMonth,
        ) => (
            <Block klpTypographyVariant="small">
                {klpSupportForm?.[`revenue${year}${row.monthNumber}`]}
            </Block>
        );

    if (!klpSupportForm) {
        return null;
    }

    // Configuration for simple key-value fields
    const contactFields = [
        {
            key: 'email',
            titleKey: 'supportForm.email.email',
            dataCy: 'SupportForm__Email',
        },
        {
            key: 'phoneNumber',
            titleKey: 'supportForm.phoneNumber.phoneNumber',
            dataCy: 'SupportForm__PhoneNumber',
        },
        {
            key: 'personApplying',
            titleKey: 'supportForm.personApplying.personName',
            dataCy: 'SupportForm__PersonApplying',
        },
    ];

    const companyFields = [
        {
            key: 'companyName',
            titleKey: 'supportForm.company.companyName',
            dataCy: 'SupportForm__CompanyName',
        },
        {
            key: 'storeName',
            titleKey: 'supportForm.store.storeName',
            dataCy: 'SupportForm__StoreName',
        },
        {
            key: 'orgNumber',
            titleKey: 'supportForm.company.orgNumber',
            dataCy: 'SupportForm__OrgNumber',
        },
        {
            key: 'kundeNummer',
            titleKey: 'supportForm.kundeNummer.clientNumber',
            dataCy: 'SupportForm__ClientNumber',
        },
        {
            key: 'invoiceNumber',
            titleKey: 'supportForm.invoice2020Q3.invoiceNumber',
            dataCy: 'SupportForm__InvoiceNumber',
        },
        {
            key: 'shoppingCenter',
            titleKey: 'supportForm.shoppingCenter.shoppingCenterName',
            dataCy: 'SupportForm__ShoppingCenter',
        },
    ];

    return (
        <Grid container={true}>
            <Grid item={true} xs={12}>
                <Heading
                    klpHeadingLevel="ONE"
                    translation="supportForm.title"
                    marginTop="bulky"
                />
            </Grid>
            <Grid item={true} xs={12}>
                <Heading
                    klpHeadingLevel="THREE"
                    translation="supportForm.contact"
                    marginTop="bulky"
                    marginBottom="bulky"
                />
            </Grid>

            <Grid item={true} xs={12}>
                {contactFields.map(
                    (field) =>
                        klpSupportForm[
                            field.key as keyof typeof klpSupportForm
                        ] && (
                            <KeyValueItems key={field.key}>
                                <KeyValueItem title={field.titleKey}>
                                    {
                                        klpSupportForm[
                                            field.key as keyof typeof klpSupportForm
                                        ]
                                    }
                                </KeyValueItem>
                            </KeyValueItems>
                        ),
                )}
            </Grid>

            <Grid item={true} xs={12}>
                <Heading
                    klpHeadingLevel="THREE"
                    translation="supportForm.leietakerinformasjon"
                    marginTop="jumbo"
                    marginBottom="bulky"
                />
            </Grid>
            <Grid item={true} xs={12}>
                {companyFields.map(
                    (field) =>
                        klpSupportForm[
                            field.key as keyof typeof klpSupportForm
                        ] && (
                            <KeyValueItems key={field.key}>
                                <KeyValueItem title={field.titleKey}>
                                    {
                                        klpSupportForm[
                                            field.key as keyof typeof klpSupportForm
                                        ]
                                    }
                                </KeyValueItem>
                            </KeyValueItems>
                        ),
                )}
            </Grid>

            <Grid item={true} xs={12}>
                <Heading
                    klpHeadingLevel="THREE"
                    translation="supportForm.support"
                    marginTop="jumbo"
                    marginBottom="bulky"
                />
            </Grid>
            <Grid item={true} xs={12}>
                <KeyValueItems>
                    <KeyValueItem title="supportForm.governmentSupport.governmentSupport">
                        {klpSupportForm.hasGovernmentSupport
                            ? t('general.confirm')
                            : t('general.decline')}
                    </KeyValueItem>
                </KeyValueItems>

                {klpSupportForm.governmentSupportExplanation && (
                    <KeyValueItems>
                        <KeyValueItem title="supportForm.governmentSupport.governmentSupportExplanation">
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(
                                        klpSupportForm.governmentSupportExplanation,
                                    ),
                                }}
                            />
                        </KeyValueItem>
                    </KeyValueItems>
                )}
            </Grid>

            <Grid item={true} xs={12}>
                <Heading
                    klpHeadingLevel="THREE"
                    translation="supportForm.economy"
                    marginTop="jumbo"
                    marginBottom="bulky"
                />
            </Grid>

            <Grid item={true} xs={12}>
                <Grid container={true}>
                    <Grid item={true} xs={12} sm={10} md={8} lg={6}>
                        <Table
                            id="SupportFormMonths"
                            data={governmentSupportMonths}
                            columns={[
                                new TableColumnDef({
                                    key: 'month',
                                    translation:
                                        'supportForm.governmentSupport.month',
                                    align: 'left',
                                }),
                                new TableColumnDef({
                                    key: 'caseNumber',
                                    translation:
                                        'supportForm.governmentSupport.caseNumber',
                                    render: renderGovernmentSupportCaseNumber,
                                }),
                                new TableColumnDef({
                                    key: 'supportSum',
                                    translation:
                                        'supportForm.governmentSupport.amount',
                                    render: renderGovernmentSupportAmount,
                                }),
                            ]}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item={true} xs={12}>
                <Heading
                    klpHeadingLevel="THREE"
                    translation="supportForm.revenue.title"
                    marginTop="jumbo"
                    marginBottom="bulky"
                />
            </Grid>

            <Grid item={true} xs={12}>
                <Grid container={true}>
                    <Grid item={true} xs={12} sm={10} md={8} lg={6}>
                        <Table<IRevenueMonth, unknown>
                            id="SupportForm"
                            data={revenueMonths}
                            columns={[
                                new TableColumnDef<IRevenueMonth, unknown>({
                                    key: 'month',
                                    translation: 'supportForm.revenue.month',
                                    align: 'left',
                                }),
                                new TableColumnDef<IRevenueMonth, unknown>({
                                    key: 'revenue2019',
                                    translation:
                                        'supportForm.revenue.revenue2019',
                                    render: renderRevenue(2019),
                                }),
                                new TableColumnDef<IRevenueMonth, unknown>({
                                    key: 'revenue2020',
                                    translation:
                                        'supportForm.revenue.revenue2020',
                                    render: renderRevenue(2020),
                                }),
                            ]}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item={true} xs={12}>
                <Heading
                    klpHeadingLevel="THREE"
                    translation="supportForm.attachments.title"
                    marginTop="jumbo"
                    marginBottom="bulky"
                />
            </Grid>

            <Grid item={true} xs={12}>
                <FileGallery
                    source={
                        klpSupportForm.supportFormFiles as Array<FileObject>
                    }
                    allowedMimeTypes={MimeTypes.document}
                    path={supportFormApiRoutes.formFiles(klpSupportForm.id)}
                    queueName="supportFormFiles"
                />
            </Grid>

            <Grid item={true} xs={12}>
                <Heading
                    klpHeadingLevel="THREE"
                    translation="supportForm.other.title"
                    marginTop="jumbo"
                    marginBottom="bulky"
                />
            </Grid>
            <Grid item={true} xs={12}>
                {klpSupportForm.klpSupportReason && (
                    <KeyValueItems>
                        <KeyValueItem title="supportForm.other.klpSupportReasonTitleShort">
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(
                                        klpSupportForm.klpSupportReason,
                                    ),
                                }}
                            />
                        </KeyValueItem>
                    </KeyValueItems>
                )}
            </Grid>

            <Grid item={true} xs={12}>
                <Block marginTop="jumbo" marginBottom="bulky">
                    <Grid container={true} justifyContent="flex-start">
                        <Grid item={true}>
                            <Form onSubmit={handleSubmit} values={{}}>
                                <FormButton
                                    name="Form"
                                    actionType="submit"
                                    disableOnError={true}
                                    translation="general.edit"
                                />
                            </Form>
                        </Grid>
                    </Grid>
                </Block>
            </Grid>
        </Grid>
    );
};

KlpSupportFormView.displayName = 'KlpSupportForm';
